import React, { useState } from 'react';
import { Button } from './ui/button';
import { Facebook, Linkedin, Mail, MessageSquare, Link2, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';

// Consistent "Share Listing" control used across public listings and dashboards.
// Links go directly to the listing URL. Wording is always "Share Listing".
export const ShareListing = ({ url, title = 'My INASW Listing', compact = false, label = 'Share Listing' }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const text = `${title} — on I Need A Social Worker`;
  const enc = encodeURIComponent;

  // Tag each share link so inbound clicks are attributed to the right source in Analytics.
  const tagged = (src) => {
    try {
      const u = new URL(shareUrl, typeof window !== 'undefined' ? window.location.origin : 'https://www.ineedasocialworker.com');
      u.searchParams.set('utm_source', src);
      u.searchParams.set('utm_medium', 'social');
      u.searchParams.set('utm_campaign', 'share_listing');
      return u.toString();
    } catch { return shareUrl; }
  };

  const targets = [
    { key: 'facebook', label: 'Facebook', Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(tagged('facebook'))}` },
    { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(tagged('linkedin'))}` },
    { key: 'x', label: 'X', Icon: Share2, href: `https://twitter.com/intent/tweet?url=${enc(tagged('twitter'))}&text=${enc(text)}` },
    { key: 'email', label: 'Email', Icon: Mail, href: `mailto:?subject=${enc(text)}&body=${enc(tagged('email'))}` },
    { key: 'sms', label: 'Text/SMS', Icon: MessageSquare, href: `sms:?&body=${enc(text + ' ' + tagged('sms'))}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Listing link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div data-testid="share-listing" className={compact ? 'flex flex-wrap items-center gap-2' : 'space-y-2'}>
      {!compact && (
        <p className="text-sm font-semibold text-primary flex items-center gap-1">
          <Share2 className="w-4 h-4" /> {label}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {targets.map(({ key, label, Icon, href }) => (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer"
             data-testid={`share-${key}`} title={label}>
            <Button type="button" variant="outline" size="sm" className="gap-1.5">
              <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
            </Button>
          </a>
        ))}
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={copy} data-testid="share-copy">
          {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Link2 className="w-4 h-4" />}
          <span className="hidden sm:inline">Copy Link</span>
        </Button>
      </div>
    </div>
  );
};

export default ShareListing;
